// src/router.ts
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useSessionStore } from './stores/session';
import { useSubscription } from './composables/useSubscription';
import { computed } from 'vue';


const Landing = () => import('./pages/Landing.vue');
const About = () => import('./pages/About.vue');
const Services = () => import('./pages/Services.vue');
const Contact = () => import('./pages/Contact.vue');
const Pricing = () => import('./pages/Pricing.vue');
const DigitalMenuPlatformService = () => import('./pages/services/DigitalMenuPlatform.vue');
const TableOrderingSystemService = () => import('./pages/services/TableOrderingSystem.vue');
const ReservationSystemService = () => import('./pages/services/ReservationSystem.vue');
const CustomerLoyaltyService = () => import('./pages/services/CustomerLoyalty.vue');
const Auth = () => import('./pages/Auth.vue');
const DashboardLayout = () => import('./pages/dashboard/DashboardLayout.vue');
const Stats = () => import('./pages/dashboard/Stats.vue');
const MenuBuilder = () => import('./pages/dashboard/MenuBuilder.vue');
const QRManager = () => import('./pages/dashboard/QRManager.vue');
const Settings = () => import('./pages/dashboard/Settings.vue');
const Appearance = () => import('./pages/dashboard/Appearance.vue');
const Billing = () => import('./pages/dashboard/Billing.vue');
const Maintenance = () => import('./pages/dashboard/Maintenance.vue');
const PublicMenu = () => import('./pages/PublicMenu.vue');
const Onboarding = () => import('./pages/onboarding/OnboardingNew.vue');
const Team = () => import('./pages/dashboard/Team.vue');
const AuditLog = () => import('./pages/dashboard/AuditLog.vue');
const Venues = () => import('./pages/dashboard/Venues.vue');
const AcceptInvite = () => import('./pages/AcceptInvite.vue');
const TermsOfService = () => import('./pages/TermsOfService.vue');
const PrivacyPolicy = () => import('./pages/PrivacyPolicy.vue');
const OrderingSystem = () => import('./pages/dashboard/OrderingSystem.vue');
const TableManagement = () => import('./pages/dashboard/TableManagement.vue');
const OrderManagement = () => import('./pages/dashboard/OrderManagement.vue');
const KitchenDisplay = () => import('./pages/dashboard/KitchenDisplay.vue');
const OnlineOrderSettings = () => import('./pages/dashboard/OnlineOrderSettings.vue');
const ExpressQRSettings = () => import('./pages/dashboard/ExpressQRSettings.vue');
const OnlineOrderTracking = () => import('./pages/OnlineOrderTracking.vue');
const ReservationSystem = () => import('./pages/dashboard/ReservationSystem.vue');
const ReservationManagement = () => import('./pages/dashboard/ReservationManagement.vue');
const ReservationSettings = () => import('./pages/dashboard/ReservationSettings.vue');
const PublicReservation = () => import('./pages/PublicReservation.vue');
// Loyalty components are now imported within LoyaltyDashboard.vue
const PublicLoyaltyPortal = () => import('./pages/PublicLoyaltyPortal.vue');
const PublicHome = () => import('./pages/PublicHome.vue');

// Detect custom domain at boot time (before router is created)
const _hostname = typeof window !== 'undefined' ? window.location.hostname : '';
export const isCustomDomainHost = _hostname !== 'qrbuilder.com'
  && _hostname !== 'www.qrbuilder.com'
  && _hostname !== 'auth.qrbuilder.com'
  && _hostname !== 'localhost'
  && _hostname !== '127.0.0.1'
  && !_hostname.endsWith('.web.app')
  && !_hostname.endsWith('.firebaseapp.com');

// Custom domain routes: clean URLs at /, /menu, /reservations, /loyalty
// The components detect custom domain mode via route.meta.customDomain
const customDomainRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'custom-domain-home',
    component: PublicHome,
    meta: { customDomain: true },
    // Legacy QR codes printed before /menu existed pointed at /?table=, /?src=qr,
    // /?express=true, /?mode=order — keep them working by redirecting to /menu
    // and preserving the query string.
    beforeEnter: (to) => {
      const q = to.query;
      if ('table' in q || q.src === 'qr' || 'express' in q || q.mode === 'order') {
        return { path: '/menu', query: q };
      }
    },
  },
  { path: '/menu', name: 'custom-domain-menu', component: PublicMenu, meta: { customDomain: true } },
  { path: '/reservations', name: 'custom-domain-reservations', component: PublicReservation, meta: { customDomain: true } },
  { path: '/loyalty', name: 'custom-domain-loyalty', component: PublicLoyaltyPortal, meta: { customDomain: true } },
  // Block everything else on custom domains
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

// Standard routes for qrbuilder.com
const standardRoutes: RouteRecordRaw[] = [
  { path: '/', name: 'landing', component: Landing },
  { path: '/about', name: 'about', component: About },
  { path: '/services', name: 'services', component: Services },
  { path: '/contact', name: 'contact', component: Contact },
  { path: '/pricing', name: 'pricing', component: Pricing },
  { path: '/services/digital-menu-platform', name: 'digital-menu-platform', component: DigitalMenuPlatformService },
  { path: '/services/table-ordering-system', name: 'table-ordering-system', component: TableOrderingSystemService },
  { path: '/services/reservation-system', name: 'reservation-system', component: ReservationSystemService },
  { path: '/services/customer-loyalty', name: 'customer-loyalty', component: CustomerLoyaltyService },
  { path: '/auth', name: 'auth', component: Auth },

  // Legal pages
  { path: '/terms', name: 'terms', component: TermsOfService },
  { path: '/privacy', name: 'privacy', component: PrivacyPolicy },

  // Invite must be public & top-level so the query params survive auth redirects
  { path: '/invite/:inviteId', name: 'acceptInvite', component: AcceptInvite },

  // Public menu (supports both slug and ID)
  { path: '/m/:restaurantIdOrSlug', name: 'public-menu', component: PublicMenu },

  // Public order tracking
  { path: '/m/:restaurantIdOrSlug/order/:orderId', name: 'order-tracking', component: OnlineOrderTracking },

  // Public reservation page (supports both slug and ID)
  { path: '/r/:restaurantIdOrSlug', name: 'public-reservation', component: PublicReservation },

  // Public loyalty portal (supports both slug and ID)
  { path: '/l/:restaurantIdOrSlug', name: 'public-loyalty', component: PublicLoyaltyPortal },

  // Public restaurant home/landing page (supports both slug and ID)
  { path: '/h/:restaurantIdOrSlug', name: 'public-home', component: PublicHome },

  {
    path: '/app',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      { path: 'onboarding', name: 'onboarding', component: Onboarding },
      { path: '', name: 'stats', component: Stats },
      { path: 'menu', name: 'menu', component: MenuBuilder },
      { path: 'qr', name: 'qr', component: QRManager, meta: { requiresOwner: true } },
      { path: 'settings', name: 'settings', component: Settings, meta: { requiresOwner: true } },
      { path: 'settings/maintenance', name: 'settings-maintenance', component: Maintenance, meta: { requiresOwner: true } },
      { path: 'team', name: 'team', component: Team, meta: { requiresOwner: true } },
      { path: 'team/activity', name: 'team-activity', component: AuditLog, meta: { requiresOwner: true } },
      { path: 'venues', name: 'venues', component: Venues }, // Allow all authenticated users to switch venues
      { path: 'appearance', name: 'appearance', component: Appearance, meta: { requiresOwner: true } },
      { path: 'billing', name: 'billing', component: Billing, meta: { requiresFounder: true } }, // Only founder can access billing
      // Ordering System (Ultra feature)
      { path: 'ordering', name: 'ordering', component: OrderingSystem },
      { path: 'ordering/tables', name: 'ordering-tables', component: TableManagement, meta: { requiresUltra: true } },
      { path: 'ordering/management', name: 'ordering-management', component: OrderManagement, meta: { requiresUltra: true } },
      { path: 'ordering/kitchen', name: 'ordering-kitchen', component: KitchenDisplay, meta: { requiresUltra: true } },
      { path: 'ordering/payments', name: 'ordering-payments', component: () => import('./pages/dashboard/OrderingPayments.vue'), meta: { requiresUltra: true, requiresOwner: true } },
      { path: 'ordering/online', name: 'ordering-online', component: OnlineOrderSettings, meta: { requiresUltra: true, requiresOwner: true } },
      { path: 'ordering/express', name: 'ordering-express', component: ExpressQRSettings, meta: { requiresUltra: true, requiresOwner: true } },
      // Reservation System (Ultra feature)
      { path: 'reservations', name: 'reservations', component: ReservationSystem },
      { path: 'reservations/manage', name: 'reservations-manage', component: ReservationManagement, meta: { requiresUltra: true } },
      { path: 'reservations/settings', name: 'reservations-settings', component: ReservationSettings, meta: { requiresUltra: true, requiresOwner: true } },
      // Loyalty Program (Ultra feature) - includes customers and offers
      { path: 'loyalty', name: 'loyalty', component: () => import('./pages/dashboard/LoyaltyDashboard.vue') },
      { path: 'loyalty/customers', name: 'loyalty-customers', component: () => import('./pages/dashboard/LoyaltyDashboard.vue') },
      { path: 'loyalty/offers', name: 'loyalty-offers', component: () => import('./pages/dashboard/LoyaltyDashboard.vue') },
      { path: 'loyalty/settings', name: 'loyalty-settings', component: () => import('./pages/dashboard/LoyaltyDashboard.vue') },
    ],
  },

  // Catch-all route for 404s - redirect to home
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

// Use custom domain routes when on a restaurant's own domain
const routes: RouteRecordRaw[] = isCustomDomainHost ? customDomainRoutes : standardRoutes;

const router = createRouter({
  history: createWebHistory(),
  routes,
  // keep scroll position on back/forward & preserve on refresh
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

// Router guard: Handle authentication and authorization
router.beforeEach(async (to, from, next) => {
  // Custom domain routes need no auth guards — pass through
  if (to.meta?.customDomain) {
    return next();
  }

  // Defer store access to ensure Pinia is initialized
  let session;
  try {
    session = useSessionStore();
  } catch (error) {
    // Pinia not yet initialized, allow navigation to continue
    console.warn('Pinia not yet initialized in router guard');
    return next();
  }

  // Check if we're on auth subdomain
  const hostname = window.location.hostname;
  const isAuthSubdomain = hostname === 'auth.qrbuilder.com';

  // Handle auth subdomain SEO and redirects
  if (isAuthSubdomain) {
    // Add noindex meta tag to prevent indexing
    let noindexMeta = document.querySelector('meta[name="robots"]');
    if (!noindexMeta) {
      noindexMeta = document.createElement('meta');
      noindexMeta.setAttribute('name', 'robots');
      document.head.appendChild(noindexMeta);
    }
    noindexMeta.setAttribute('content', 'noindex, nofollow');

    // If not accessing auth page or auth-related paths, redirect to main domain
    if (to.name !== 'auth' && !to.path.startsWith('/auth') && !to.query.redirect && !to.query.continue) {
      // Preserve dark mode during redirect by applying theme before navigation
      const storedTheme = localStorage.getItem('qrmb-theme-preference');
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const shouldUseDark = storedTheme === 'dark' || (storedTheme !== 'light' && systemDark);

      if (shouldUseDark) {
        document.documentElement.classList.add('dark');
        document.documentElement.style.colorScheme = 'dark';
      }

      window.location.href = `https://qrbuilder.com${to.fullPath}`;
      return;
    }
  }

  // Wait for auth initialization
  if (!session.isInitialized) {
    await session.initialize();
  }

  const requiresAuth = to.matched.some((m) => m.meta?.requiresAuth);
  const requiresOwner = to.matched.some((m) => m.meta?.requiresOwner);
  const requiresFounder = to.matched.some((m) => m.meta?.requiresFounder);
  const requiresPro = to.matched.some((m) => m.meta?.requiresPro);
  const requiresUltra = to.matched.some((m) => m.meta?.requiresUltra);

  // Handle authentication requirement
  if (requiresAuth && !session.isAuthenticated) {
    session.setRedirectPath(to.fullPath);
    return next({ name: 'auth', replace: true });
  }

  // Handle restaurant access requirement for dashboard routes
  if (requiresAuth && session.isAuthenticated && to.path.startsWith('/app')) {
    // Wait for restaurant data to load before making access decisions
    if (!session.isRestaurantLoaded) {
      const maxWaitTime = 3000;
      const startTime = Date.now();
      while (!session.isRestaurantLoaded && Date.now() - startTime < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // If user has no restaurant access, they are a phone-auth customer, not a restaurant owner/staff
    // Phone-authenticated loyalty customers should not access the dashboard at all
    if (session.isRestaurantLoaded && !session.currentRestaurantId) {
      // Check if user authenticated via phone (no email)
      const user = session.user;
      const isPhoneOnlyUser = user?.phoneNumber && !user?.email;

      if (isPhoneOnlyUser) {
        // Phone-authenticated customer trying to access dashboard - redirect to auth page
        console.log('Router: Phone-authenticated customer blocked from dashboard');
        return next({ name: 'auth', replace: true });
      }

      // Email-authenticated user without restaurant - allow onboarding
      if (to.name === 'onboarding') {
        return next();
      }
      // For other dashboard routes, redirect to onboarding
      console.log('Router: User without restaurant trying to access dashboard, redirecting to onboarding');
      return next({ name: 'onboarding', replace: true });
    }
  }

  // Handle redirect after authentication - preserve intended route
  if (to.name === 'auth' && session.isAuthenticated) {
    // Wait for restaurant data to load before making redirect decisions
    if (!session.isRestaurantLoaded) {
      const maxWaitTime = 2000;
      const startTime = Date.now();
      while (!session.isRestaurantLoaded && Date.now() - startTime < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // If user has no restaurant access
    if (session.isRestaurantLoaded && !session.currentRestaurantId) {
      // Check if phone-authenticated user (loyalty customer)
      const user = session.user;
      const isPhoneOnlyUser = user?.phoneNumber && !user?.email;

      if (isPhoneOnlyUser) {
        // Allow phone-authenticated customers to access auth page
        // This lets them sign out and log in with email if they want
        return next();
      }

      // Email-authenticated user without restaurant - redirect to onboarding
      return next({ name: 'onboarding', replace: true });
    }

    // Check session store first
    let redirectPath = session.getAndClearRedirectPath();

    // If no session redirect, check URL query parameter
    if (!redirectPath) {
      const queryRedirect = (to.query.redirect || to.query.continue) as string;
      if (queryRedirect && queryRedirect.startsWith('/')) {
        // Decode the URL to handle cases where & was encoded as %26
        redirectPath = decodeURIComponent(queryRedirect);
      }
    }

    const destination = redirectPath || (from.fullPath !== '/' ? from.fullPath : '/app');
    return next(destination);
  }

  // Handle founder-only routes
  if (requiresFounder && session.isAuthenticated) {
    // Wait for restaurant data to load if it's not loaded yet
    if (!session.isRestaurantLoaded) {
      const maxWaitTime = 3000;
      const startTime = Date.now();
      while (!session.isRestaurantLoaded && Date.now() - startTime < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Only redirect if we have restaurant data and user is not the founder
    if (session.isRestaurantLoaded) {
      const restaurant = session.currentRestaurant;
      const userId = session.user?.uid || '';

      // Determine founder: ownerUid or first owner in members
      const founderId = restaurant?.ownerUid ||
                       Object.entries(restaurant?.members || {}).find(([_, role]) => role === 'owner')?.[0] ||
                       null;

      if (userId !== founderId) {
        return next({ name: 'stats', replace: true });
      }
    }
  }

  // Handle owner-only routes
  if (requiresOwner && session.isAuthenticated) {
    // Wait for restaurant data to load if it's not loaded yet
    if (!session.isRestaurantLoaded) {
      // Wait for restaurant to actually load (max 3 seconds)
      const maxWaitTime = 3000;
      const startTime = Date.now();
      while (!session.isRestaurantLoaded && Date.now() - startTime < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Only redirect if we have restaurant data and user is not owner
    if (session.isRestaurantLoaded) {
      const userRole = session.currentRestaurant?.members?.[session.user?.uid || ''];
      if (userRole !== 'owner') {
        return next({ name: 'stats', replace: true });
      }
    }
  }

  // Handle Pro-only routes
  if (requiresPro && session.isAuthenticated) {
    // Wait for restaurant data to load if it's not loaded yet
    if (!session.isRestaurantLoaded) {
      const maxWaitTime = 3000;
      const startTime = Date.now();
      while (!session.isRestaurantLoaded && Date.now() - startTime < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Check Pro subscription status
    if (session.isRestaurantLoaded) {
      const subscription = useSubscription(
        session.currentRestaurant,
        computed(() => ({ categoriesCount: 0, itemsCount: 0 }))
      );

      // Redirect to stats page if not Pro (or higher)
      if (!subscription?.isProOrHigher?.value) {
        return next({ name: 'stats', replace: true });
      }
    }
  }

  // Handle Ultra-only routes
  if (requiresUltra && session.isAuthenticated) {
    // Wait for restaurant data to load if it's not loaded yet
    if (!session.isRestaurantLoaded) {
      const maxWaitTime = 3000;
      const startTime = Date.now();
      while (!session.isRestaurantLoaded && Date.now() - startTime < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Check Ultra subscription status
    if (session.isRestaurantLoaded) {
      const subscription = useSubscription(
        session.currentRestaurant,
        computed(() => ({ categoriesCount: 0, itemsCount: 0 }))
      );

      // Redirect to stats page if not Ultra (or in Ultra grace period)
      if (!subscription?.isUltra?.value && !subscription?.isInUltraGrace?.value) {
        return next({ name: 'stats', replace: true });
      }
    }
  }

  // Handle onboarding flow - only if restaurant data is actually loaded
  if (session.isAuthenticated && to.path.startsWith('/app')) {
    console.log('Router onboarding check:', {
      isRestaurantLoaded: session.isRestaurantLoaded,
      isOnboarded: session.isOnboarded,
      currentRestaurantId: session.currentRestaurantId,
      toName: to.name,
      fromName: from.name
    });

    // Wait for restaurant data to be loaded before making onboarding decisions
    // This prevents flash of onboarding screen during refresh
    if (session.isRestaurantLoaded) {
      // Restaurant data is loaded, make onboarding decisions
      if (!session.isOnboarded && to.name !== 'onboarding') {
        console.log('Router: Redirecting to onboarding');
        return next({ name: 'onboarding', replace: true });
      }
      if (session.isOnboarded && to.name === 'onboarding') {
        console.log('Router: Redirecting away from onboarding to stats');
        return next({ name: 'stats', replace: true });
      }
    }
    // If restaurant data is still loading, allow navigation - the app will handle loading states
  }

  next();
});

export default router;
